import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Vault, CheckCircle, Lock, UserPlus, X, CloudArrowUp,
  FilePdf, FileImage, Trash, Plus,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const empty = {
  business_name: "", dba: "", entity_type: "LLC", ein: "", industry: "", years_in_business: "",
  annual_revenue: "", requested_amount: "", business_address: "",
  owner_name: "", owner_email: "", owner_phone: "", owner_ssn: "", owner_dob: "",
  owner_ownership_pct: "", owner_home_address: "",
  owner2_name: "", owner2_email: "", owner2_phone: "", owner2_ssn: "", owner2_dob: "",
  owner2_ownership_pct: "", owner2_home_address: "", notes: "",
};

const DOC_TYPES = [
  { key: "bank_statements", label: "Last 4 Months Bank Statements", hint: "PDF preferred · multiple files welcome", multiple: true },
  { key: "drivers_license", label: "Driver's License (front & back)", hint: "Photo or scan of both sides", multiple: true },
  { key: "voided_check", label: "Voided Business Check", hint: "Clear photo or PDF", multiple: true },
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif";
const fileOk = (f) => {
  const ext = f.name.split(".").pop().toLowerCase();
  return ["pdf", "jpg", "jpeg", "png", "heic", "heif"].includes(ext) && f.size <= 25 * 1024 * 1024;
};

export default function Apply() {
  const [brand, setBrand] = useState({ company_name: "Business Line OC" });
  const [f, setF] = useState(empty);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState({ bank_statements: [], drivers_license: [], voided_check: [] });
  const [showOwner2, setShowOwner2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [done, setDone] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(0);

  useEffect(() => { api.get("/settings").then((r) => setBrand((p) => ({ ...p, ...r.data, company_name: r.data.company_name || p.company_name }))).catch(() => {}); }, []);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: false })); };
  const errCls = (k) => (errors[k] ? " border-destructive ring-1 ring-destructive/30" : "");

  const addFiles = (docType, fileList) => {
    const arr = Array.from(fileList);
    const valid = arr.filter(fileOk);
    if (valid.length < arr.length) alert("Some files were skipped (only PDF/JPG/PNG/HEIC up to 25MB).");
    setFiles((p) => ({ ...p, [docType]: [...p[docType], ...valid] }));
  };
  const removeFile = (docType, idx) => setFiles((p) => ({ ...p, [docType]: p[docType].filter((_, i) => i !== idx) }));

  const submit = async () => {
    const e = {};
    if (!f.business_name) e.business_name = true;
    if (!f.owner_name) e.owner_name = true;
    if (!f.owner_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.owner_email)) e.owner_email = true;
    if (!f.requested_amount) e.requested_amount = true;
    setErrors(e);
    if (Object.keys(e).length) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setLoading(true);
    try {
      setProgress("Submitting application…");
      const payload = { ...f };
      if (!showOwner2) {
        ["owner2_name", "owner2_email", "owner2_phone", "owner2_ssn", "owner2_dob", "owner2_ownership_pct", "owner2_home_address"].forEach((k) => (payload[k] = ""));
      }
      const { data } = await api.post("/applications", payload);
      // Uploads are optional — never let a failed upload block a saved application.
      const allFiles = DOC_TYPES.flatMap((d) => files[d.key].map((file) => ({ docType: d.key, file })));
      let n = 0, failed = 0;
      for (const item of allFiles) {
        n += 1;
        setProgress(`Uploading documents (${n}/${allFiles.length})…`);
        try {
          const fd = new FormData();
          fd.append("file", item.file);
          await api.post(`/applications/${data.id}/documents?doc_type=${item.docType}`, fd, {
            headers: { Authorization: `Bearer ${data.upload_token}`, "Content-Type": "multipart/form-data" },
          });
        } catch {
          failed += 1;
        }
      }
      setUploadFailed(failed);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      alert("We couldn't submit your application just now. Please try again in a moment — if it keeps happening, contact us and we'll help you finish.");
    } finally { setLoading(false); setProgress(""); }
  };

  const inputCls = "bg-secondary border-border h-11";

  if (done)
    return (
      <div className="theme-admin min-h-screen bg-background text-foreground relative flex items-center justify-center px-4">
        <div className="absolute inset-0 mesh-glow" aria-hidden />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative text-center max-w-md">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-primary/12 flex items-center justify-center mb-6">
            <CheckCircle size={34} weight="duotone" className="text-primary" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="apply-success">Application received</h1>
          <p className="text-muted-foreground mt-3">
            Thanks, {f.owner_name.split(" ")[0] || "there"}. Our team will review your request for{" "}
            <span className="text-foreground">{f.business_name}</span> and reach out shortly with your offer.
          </p>
          {uploadFailed > 0 && (
            <p className="text-xs text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mt-4">
              Your application was received, but {uploadFailed} document{uploadFailed > 1 ? "s" : ""} didn't finish uploading. No problem — our team will request {uploadFailed > 1 ? "them" : "it"} directly.
            </p>
          )}
          <Link to="/"><Button variant="outline" className="mt-7"><ArrowLeft size={16} className="mr-1" /> Back to home</Button></Link>
        </motion.div>
      </div>
    );

  return (
    <div className="theme-admin min-h-screen bg-background text-foreground relative">
      <div className="absolute inset-0 dot-grid opacity-40" aria-hidden />
      <div className="absolute inset-x-0 top-0 h-96 mesh-glow" aria-hidden />

      <header className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Vault size={20} weight="duotone" className="text-primary" />
          </div>
          <span className="font-display font-bold">{brand.company_name}</span>
        </Link>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><ArrowLeft size={15} /> Home</Link>
      </header>

      <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 pb-20 pt-6">
        <div className="overline text-primary">Application</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Merchant Cash Advance / LOC Application</h1>
        <p className="text-muted-foreground mt-2">Complete your application and attach documents to speed up approval. Uploads are optional — you can send them later if you prefer.</p>

        <div className="mt-8 space-y-5">
          {/* Business */}
          <Card title="Business Information">
            <div className="grid md:grid-cols-2 gap-4">
              <Fld label="Legal Business Name *"><Input data-testid="apply-business-name" className={inputCls + errCls("business_name")} value={f.business_name} onChange={(e) => set("business_name", e.target.value)} /></Fld>
              <Fld label="DBA (optional)"><Input className={inputCls} value={f.dba} onChange={(e) => set("dba", e.target.value)} /></Fld>
              <Fld label="Entity Type">
                <Select value={f.entity_type} onValueChange={(v) => set("entity_type", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>{["LLC", "Corp", "Sole Prop", "Partnership"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="EIN (Federal Tax ID)"><Input data-testid="apply-ein" className={inputCls + " num"} value={f.ein} onChange={(e) => set("ein", e.target.value)} placeholder="12-3456789" /></Fld>
              <Fld label="Industry"><Input className={inputCls} value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Restaurant" /></Fld>
              <Fld label="Years in Business"><Input className={inputCls + " num"} value={f.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} /></Fld>
              <Fld label="Estimated Annual Revenue ($)"><Input className={inputCls + " num"} value={f.annual_revenue} onChange={(e) => set("annual_revenue", e.target.value)} placeholder="750000" /></Fld>
              <Fld label="Requested Amount ($) *"><Input data-testid="apply-amount" className={inputCls + " num" + errCls("requested_amount")} value={f.requested_amount} onChange={(e) => set("requested_amount", e.target.value)} placeholder="150000" /></Fld>
              <div className="md:col-span-2"><Fld label="Business Address"><Input className={inputCls} value={f.business_address} onChange={(e) => set("business_address", e.target.value)} /></Fld></div>
            </div>
          </Card>

          {/* Owner */}
          <Card title="Owner / Guarantor">
            <div className="grid md:grid-cols-2 gap-4">
              <Fld label="Full Name *"><Input data-testid="apply-owner-name" className={inputCls + errCls("owner_name")} value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} /></Fld>
              <Fld label="Ownership %"><Input className={inputCls + " num"} value={f.owner_ownership_pct} onChange={(e) => set("owner_ownership_pct", e.target.value)} placeholder="100" /></Fld>
              <Fld label="Email *"><Input data-testid="apply-owner-email" className={inputCls + errCls("owner_email")} type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} /></Fld>
              <Fld label="Phone"><Input className={inputCls} value={f.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} /></Fld>
              <Fld label="Social Security Number"><Input data-testid="apply-ssn" className={inputCls + " num"} value={f.owner_ssn} onChange={(e) => set("owner_ssn", e.target.value)} placeholder="###-##-####" /></Fld>
              <Fld label="Date of Birth"><Input data-testid="apply-dob" className={inputCls + " num"} type="date" value={f.owner_dob} onChange={(e) => set("owner_dob", e.target.value)} /></Fld>
              <div className="md:col-span-2"><Fld label="Home Address"><Input className={inputCls} value={f.owner_home_address} onChange={(e) => set("owner_home_address", e.target.value)} /></Fld></div>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
              <Lock size={13} /> Your SSN is encrypted and only the last 4 digits are ever displayed to our underwriting team.
            </div>
          </Card>

          {/* Owner 2 */}
          {showOwner2 ? (
            <Card title="Second Owner / Guarantor" action={<button onClick={() => setShowOwner2(false)} className="text-muted-foreground hover:text-destructive" data-testid="remove-owner2"><X size={18} /></button>}>
              <div className="grid md:grid-cols-2 gap-4">
                <Fld label="Full Name"><Input data-testid="apply-owner2-name" className={inputCls} value={f.owner2_name} onChange={(e) => set("owner2_name", e.target.value)} /></Fld>
                <Fld label="Ownership %"><Input className={inputCls + " num"} value={f.owner2_ownership_pct} onChange={(e) => set("owner2_ownership_pct", e.target.value)} /></Fld>
                <Fld label="Email"><Input className={inputCls} type="email" value={f.owner2_email} onChange={(e) => set("owner2_email", e.target.value)} /></Fld>
                <Fld label="Phone"><Input className={inputCls} value={f.owner2_phone} onChange={(e) => set("owner2_phone", e.target.value)} /></Fld>
                <Fld label="Social Security Number"><Input className={inputCls + " num"} value={f.owner2_ssn} onChange={(e) => set("owner2_ssn", e.target.value)} placeholder="###-##-####" /></Fld>
                <Fld label="Date of Birth"><Input className={inputCls + " num"} type="date" value={f.owner2_dob} onChange={(e) => set("owner2_dob", e.target.value)} /></Fld>
                <div className="md:col-span-2"><Fld label="Home Address"><Input className={inputCls} value={f.owner2_home_address} onChange={(e) => set("owner2_home_address", e.target.value)} /></Fld></div>
              </div>
            </Card>
          ) : (
            <button onClick={() => setShowOwner2(true)} data-testid="add-owner2" className="w-full rounded-[var(--radius)] border border-dashed border-border bg-card/40 hover:border-primary/40 transition-colors py-4 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <UserPlus size={18} /> Add a second owner / guarantor
            </button>
          )}

          {/* Documents */}
          <Card title="Documents" subtitle="Optional — attach now to speed things up, or send later.">
            <div className="space-y-4">
              {DOC_TYPES.map((d) => (
                <DocUpload key={d.key} doc={d} files={files[d.key]} onAdd={(fl) => addFiles(d.key, fl)} onRemove={(i) => removeFile(d.key, i)} />
              ))}
            </div>
          </Card>

          <Card title="Anything else?">
            <Textarea className="bg-secondary border-border" value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Tell us how you plan to use the capital, or anything we should know." />
          </Card>

          <Button data-testid="apply-submit" onClick={submit} disabled={loading} className="w-full h-12 font-semibold text-base">
            {loading ? (progress || "Submitting…") : "Submit Application"} {!loading && <ArrowRight size={18} className="ml-1.5" />}
          </Button>
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5"><Lock size={13} /> Bank-grade encryption. Reviewed by our underwriting team only.</p>
        </div>
      </div>
    </div>
  );
}

function DocUpload({ doc, files, onAdd, onRemove }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-medium">{doc.label}</div>
          <div className="text-[11px] text-muted-foreground">{doc.hint}</div>
        </div>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onAdd(e.dataTransfer.files); }}
        onClick={() => ref.current?.click()}
        className={`rounded-lg border border-dashed cursor-pointer py-4 flex flex-col items-center gap-1 transition-colors ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
        data-testid={`apply-upload-${doc.key}`}
      >
        <CloudArrowUp size={22} weight="duotone" className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Drop files or tap to upload</span>
        <input ref={ref} type="file" multiple={doc.multiple} accept={ACCEPT} className="hidden" onChange={(e) => onAdd(e.target.files)} data-testid={`apply-input-${doc.key}`} />
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {files.map((file, i) => {
            const Icon = file.name.toLowerCase().endsWith(".pdf") ? FilePdf : FileImage;
            return (
              <div key={i} className="flex items-center gap-2 text-xs bg-background rounded-lg px-2.5 py-1.5">
                <Icon size={15} weight="duotone" className="text-primary" />
                <span className="truncate flex-1">{file.name}</span>
                <span className="num text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)}MB</span>
                <button onClick={(e) => { e.stopPropagation(); onRemove(i); }} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Card = ({ title, subtitle, action, children }) => (
  <div className="rounded-[var(--radius)] border border-border bg-card card-inner-glow p-6">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="overline text-muted-foreground">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground mt-1 normal-case tracking-normal">{subtitle}</div>}
      </div>
      {action}
    </div>
    {children}
  </div>
);
const Fld = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="overline text-muted-foreground">{label}</Label>
    {children}
  </div>
);
